import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTouchAgent } from "@/hooks/use-agent-prefs";

/**
 * Switch the sidebar and chat to an agent, exactly like clicking it in the
 * sidebar's agent list (`syncAgentToUrl` in tenant-sidebar.tsx): a new chat
 * with that agent, recorded as last used. The URL's `agentId` is what the
 * sidebar highlights and the chat talks to, so anything that "opens" an agent
 * outside the sidebar must go through here or the two fall out of step.
 *
 * Reads the tenant from the route, so it also works in TenantShell, which
 * sits outside TenantProvider.
 */
export function useOpenAgentChat() {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const touchAgent = useTouchAgent();
  const { mutate: touch } = touchAgent;

  return useCallback(
    (agentId: string) => {
      if (!tenantId) return;
      const current = new URLSearchParams(window.location.search).get("agentId");
      if (agentId === current && window.location.pathname.endsWith("/workspace")) return;
      touch(agentId);
      const params = new URLSearchParams();
      params.set("agentId", agentId);
      params.set("newChat", "1");
      navigate(`/tenants/${tenantId}/workspace?${params.toString()}`);
    },
    [navigate, tenantId, touch],
  );
}
